import { useMemo, useRef } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';

// objectID is Algolia's record id (always on a real hit). product_id is the
// catalog attribute we retrieve; connector cache hits can omit objectID during
// InstantSearch timing gaps, so fall back to it for the freshness compare.
const hitId = hit => String(hit?.objectID ?? hit?.product_id ?? '');
const sameHitIds = (left, right) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (hitId(left[i]) !== hitId(right[i])) return false;
  }
  return true;
};

/**
 * Wraps useInfiniteHits with a self-managed accumulation fallback for
 * React 19 + Next.js App Router environments where the connector's internal
 * cache can fail to populate due to deferred setState timing.
 *
 * Prefers the connector when its full ID list matches the accumulated
 * results.hits pages; otherwise uses the accumulator so filter changes cannot
 * leave stale extra connector pages on screen while nbHits already updated.
 */
const useHitsAccumulation = () => {
  const {
    items: connectorItems,
    showMore,
    isLastPage: connectorIsLastPage
  } = useInfiniteHits({
    skipSuspense: true
  });
  const {
    results
  } = useInstantSearch({
    skipSuspense: true
  });
  const accRef = useRef({
    hits: {},
    key: ''
  });
  const items = useMemo(() => {
    // InstantSearch stamps placeholder/empty results with __isArtificial before
    // a real Algolia response arrives — those must not seed the accumulator.
    // eslint-disable-next-line no-underscore-dangle
    const hasRealResults = !!results?.hits?.length && !results.__isArtificial;
    if (hasRealResults) {
      // The response identity: index name and params minus the page.
      const paramsWithoutPage = (results.params || '').split('&').filter(p => !p.startsWith('page=')).join('&');
      const key = `${results.index ?? ''}-${results.query ?? ''}-${paramsWithoutPage}`;
      const page = results.page ?? 0;
      if (key !== accRef.current.key) {
        // A search always starts at page 0, so a new identity on page 0 is a
        // new search (query, filter, sort, Show All hitsPerPage) and the
        // accumulation restarts. A new identity on a later page is the same
        // search continuing after its identity shifted: the first client
        // request after a hydrated load, whose page 0 came from the server
        // under the server's index name and params (CON-8837). Resetting
        // there discarded page 0, and the auto-advance effect in
        // AdvancedSearchProductHits then fetched page 2 on top of it.
        if (page === 0) {
          accRef.current = {
            hits: {},
            key
          };
        } else {
          accRef.current.key = key;
        }
      }
      accRef.current.hits[page] = results.hits;
      const accumulated = Object.keys(accRef.current.hits).map(Number).sort((a, b) => a - b).flatMap(p_0 => accRef.current.hits[p_0]);
      if (connectorItems?.length > 0) {
        // Full list, not just page 0 — a filter change can keep the first
        // page and still leave extra stale pages in the connector cache.
        if (sameHitIds(accumulated, connectorItems)) {
          return connectorItems;
        }
        return accumulated;
      }
      return accumulated;
    }
    return connectorItems;
  }, [connectorItems, results]);
  const isLastPage = items === connectorItems && connectorItems?.length > 0 ? connectorIsLastPage : results?.nbPages != null && results?.page != null ? results.nbPages <= results.page + 1 : connectorIsLastPage;
  return {
    items,
    isLastPage,
    showMore
  };
};
export default useHitsAccumulation;